import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function Expenses() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7) + "-01";
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "Rent", amount: "", date: today, payee: "", reference: "", payment_method: "cash", notes: "", paid: true });

  const load = async () => {
    const params = { from_date: fromDate, to_date: toDate };
    const [list, rep] = await Promise.all([api.get("/expenses", { params }), api.get("/reports/expenses", { params })]);
    setItems(list.data); setSummary(rep.data);
  };
  useEffect(() => { load(); }, [fromDate, toDate]); // eslint-disable-line
  useEffect(() => { api.get("/expense-categories").then(r => setCats(r.data)); }, []);

  const save = async () => {
    if (!f.category || !parseFloat(f.amount)) { toast.error("Category and amount required"); return; }
    try {
      await api.post("/expenses", { ...f, amount: parseFloat(f.amount) });
      toast.success("Expense logged");
      setOpen(false);
      setF({ category: "Rent", amount: "", date: today, payee: "", reference: "", payment_method: "cash", notes: "", paid: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete expense?")) return;
    try { await api.delete(`/expenses/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Outflows</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Expenses</h1>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-40" data-testid="exp-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-40" data-testid="exp-to" /></div>
          <Button onClick={() => setOpen(true)} data-testid="new-expense-btn"><Plus className="w-4 h-4 mr-1" />Log expense</Button>
        </div>
      </div>

      {summary && (
        <div className="grid sm:grid-cols-3 gap-3">
          <KPI label="Total expenses" value={inr(summary.total)} icon={Wallet} accent />
          <KPI label="Entries" value={summary.count} />
          <KPI label="Top category" value={summary.by_category[0] ? `${summary.by_category[0].category} · ${inr(summary.by_category[0].total)}` : "—"} />
        </div>
      )}

      <Card className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Payee</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(e => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="font-mono text-sm">{e.date}</TableCell>
                <TableCell><Badge variant="outline">{e.category}</Badge></TableCell>
                <TableCell className="text-sm">{e.payee || "—"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{inr(e.amount)}</TableCell>
                <TableCell className="text-xs uppercase">{e.payment_method || "—"}</TableCell>
                <TableCell>{e.paid ? <Badge variant="secondary">paid</Badge> : <Badge variant="destructive">unpaid</Badge>}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => del(e.id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No expenses in this range.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>Log expense</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Category</Label>
                <Select value={f.category} onValueChange={v => setF(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Amount *</Label><Input type="number" value={f.amount} onChange={e => setF(p => ({ ...p, amount: e.target.value }))} data-testid="exp-amount" autoFocus /></div>
              <div><Label>Date</Label><Input type="date" value={f.date} onChange={e => setF(p => ({ ...p, date: e.target.value }))} /></div>
              <div><Label>Payee</Label><Input value={f.payee} onChange={e => setF(p => ({ ...p, payee: e.target.value }))} placeholder="Vendor name" /></div>
              <div><Label>Reference</Label><Input value={f.reference} onChange={e => setF(p => ({ ...p, reference: e.target.value }))} placeholder="Bill #/UTR" /></div>
              <div><Label>Payment</Label>
                <Select value={f.payment_method} onValueChange={v => setF(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["cash", "card", "upi", "bank", "other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Input value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.paid} onChange={e => setF(p => ({ ...p, paid: e.target.checked }))} /> Already paid</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-expense">Save expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KPI = ({ label, value, icon: Icon, accent }) => (
  <Card className={`p-4 ${accent ? "bg-primary/5 border-primary/30" : ""}`}>
    <div className="flex items-start justify-between">
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</div>
      {Icon && <Icon className="w-4 h-4 text-primary" />}
    </div>
    <div className="mt-2 font-heading text-2xl sm:text-3xl font-black tracking-tighter">{value}</div>
  </Card>
);
